import { useCallback, useState } from 'react';
import type { PermissionsMap } from 'vopay';
import { useApi } from '#shared/providers/Api';
import useUsers from '../useUsers';
import unwrap from '../unwrap';

// The categorized permission catalog a role is built from, with every permission reset to off, so the drawer starts with nothing selected. Rejects on failure.
export default function useRolePermissionsCatalog() {
  const api = useApi();
  const { viewer, account, users, permissionCatalogSource = 'viewer' } = useUsers();
  const readsAccountCatalog = permissionCatalogSource === 'account';
  // Any listed user answers for the account's catalog: only the keys and labels are kept, never the values.
  // Both stay undefined for the viewer's catalog, so its callback doesn't change each time the list reloads.
  const catalogUsername = readsAccountCatalog ? users[0]?.Username : undefined;
  const catalogBearer = readsAccountCatalog ? account.bearer : undefined;
  const [catalog, setCatalog] = useState<PermissionsMap>({});
  const [isFetching, setIsFetching] = useState(false);

  const fetchCatalog = useCallback(async () => {
    try {
      setIsFetching(true);

      // 'viewer' is deliberately unscoped by account.bearer: the catalog comes from the logged-in user's
      // own account permissions, even when the page is scoped to a sub-account. 'account' reads the
      // viewed account's tree instead, as that account, for one of its own users - the endpoint answers
      // only for users of the account it is called as.
      let request;

      if (readsAccountCatalog) {
        // Nothing to read it for yet (the list is loading, or failed). Logged like a failed request; the
        // callers re-run once the list lands, since this callback changes with it.
        if (!catalogUsername) {
          const error = new Error('No listed user to read the permission catalog for.');

          console.error(error);
          throw error;
        }

        request = api.fetchAccountUserPermissionsNew({ EmailAddress: catalogUsername }, catalogBearer);
      } else {
        request = api.fetchAccountUserPermissionsNew({ EmailAddress: viewer.username });
      }

      const fetched = (await unwrap(request)).Permissions;
      const blankCatalog: PermissionsMap = {};

      Object.entries(fetched).forEach(([groupName, group]) => {
        blankCatalog[groupName] = {};

        Object.entries(group).forEach(([permissionKey, item]) => {
          blankCatalog[groupName][permissionKey] = { label: item.label, tooltip: item.tooltip, permitted: false };
        });
      });

      setCatalog(blankCatalog);
    } finally {
      setIsFetching(false);
    }
  }, [api, viewer.username, readsAccountCatalog, catalogUsername, catalogBearer]);

  return { catalog, isFetching, fetchCatalog };
}
